import { defineStore } from "pinia"
import {
   getOnboardingStatus,
   markTourCompleted,
   resetTourCompletion,
   resetAllTours,
} from "@/services/adminBackendClient"
import { collectError } from "@/utils"
import { collectMessage } from "@utils/errors"
import { clerk } from "@/services/clerk"

// Interface for shepherd tour instance
interface TourInstance {
   isActive(): boolean
   cancel(): void
}

/**
 * Tour store state interface
 */
type TourState = {
   /** Record of tour IDs mapped to their completion status */
   tours: Record<string, boolean>
   /** Flag to track if the store has been initialized */
   initialized: boolean
   /** ID of the currently active tour, or null if no tour is active */
   activeTourId: string | null
   /** Active tour instances for global management */
   activeTourInstances: Record<string, TourInstance>
   /** Timeout IDs for pending tours */
   pendingTourTimeouts: Record<string, number>
}
export const useTourStore = defineStore("tourStore", {
   state: (): TourState => {
      return {
         tours: {},
         initialized: false,
         activeTourId: null,
         activeTourInstances: {},
         pendingTourTimeouts: {},
      }
   },
   persist: true,
   actions: {
      /**
       * Initialize the tour store by fetching completion status from API
       */
      async initialize(): Promise<void> {
         if (this.initialized) return

         try {
            const response = await getOnboardingStatus()
            this.tours = response.data.tours || {}
            this.initialized = true
         } catch (error) {
            collectError(error as Error)
            console.error("Failed to initialize tour store:", error)
            // Initialize with empty state on error
            this.tours = {}
            this.initialized = true
         }
      },

      /**
       * Check if a specific tour has been viewed/completed
       * @param tourId - ID of the tour to check
       * @returns Boolean indicating if the tour has been viewed
       */
      async hasTourBeenViewed(tourId: string): Promise<boolean> {
         await this.initialize()
         return tourId in this.tours ? this.tours[tourId] : false
      },

      /**
       * Mark a tour as viewed/completed
       * @param tourId - ID of the tour to mark as viewed
       */
      async markTourAsViewed(tourId: string): Promise<void> {
         try {
            const response = await markTourCompleted(tourId)
            await clerk.load()

            // Update state with the response
            if (response.data && response.data.completed) {
               this.tours[tourId] = response.data.completed
            }
         } catch (error) {
            collectError(error as Error)
            console.error(`Failed to mark tour ${tourId} as viewed:`, error)

            // Optimistically update state even if API call fails
            this.tours[tourId] = true
         }
      },

      /**
       * Reset a specific tour so it can be viewed again
       * @param tourId - ID of the tour to reset
       */
      async resetTour(tourId: string): Promise<void> {
         try {
            const response = await resetTourCompletion(tourId)
            await clerk.load()

            // Update state with the response
            if (response.data) {
               this.tours[tourId] = false
            }
         } catch (error) {
            collectError(error as Error)
            console.error(`Failed to reset tour ${tourId}:`, error)

            // Optimistically update state even if API call fails
            this.tours[tourId] = false
         }
      },

      /**
       * Reset all tours so they can be viewed again
       */
      async resetAllTours(): Promise<void> {
         try {
            await resetAllTours()
            await clerk.load()

            // Clear the tour completion status
            this.tours = {}
         } catch (error) {
            collectError(error as Error)
            console.error("Failed to reset all tours:", error)

            // Optimistically clear state even if API call fails
            this.tours = {}
         }
      },

      /**
       * Get the current tour completion status
       * @returns Record of tour IDs to completion status
       */
      async getTourCompletionStatus(): Promise<Record<string, boolean>> {
         await this.initialize()
         return { ...this.tours }
      },

      /**
       * Start a specific tour, ensuring only one tour can run at a time
       * @param tourId - ID of the tour to start
       * @returns Boolean indicating if the tour was successfully started
       */
      startTour(tourId: string): boolean {
         // If another tour is already running, don't start this one
         if (this.activeTourId !== null && this.activeTourId !== tourId) {
            console.warn(
               `Cannot start tour ${tourId}, tour ${this.activeTourId} is already running`,
            )
            return false
         }

         // Set this tour as the active tour
         this.activeTourId = tourId
         return true
      },

      /**
       * End the currently active tour
       * @param tourId - ID of the tour to end (must match currently active tour)
       * @returns Boolean indicating if the tour was successfully ended
       */
      endTour(tourId: string): boolean {
         // Only end the tour if it matches the active tour
         if (this.activeTourId !== tourId) {
            console.warn(`Cannot end tour ${tourId}, it is not the active tour`)
            collectMessage("Cannot end tour, it is not the active tour")
            return false
         }

         this.activeTourId = null
         return true
      },

      /**
       * Check if any tour is currently active
       * @returns Boolean indicating if any tour is currently active
       */
      isAnyTourActive(): boolean {
         return this.activeTourId !== null
      },

      /**
       * Get the ID of the currently active tour
       * @returns ID of the active tour or null if no tour is active
       */
      getActiveTourId(): string | null {
         return this.activeTourId
      },

      /**
       * Register a tour instance for global management
       * @param tourId - ID of the tour to register
       * @param tourInstance - The shepherd tour instance
       */
      registerTourInstance(tourId: string, tourInstance: TourInstance): void {
         this.activeTourInstances[tourId] = tourInstance
      },

      /**
       * Unregister a tour instance
       * @param tourId - ID of the tour to unregister
       */
      unregisterTourInstance(tourId: string): void {
         delete this.activeTourInstances[tourId]
      },

      /**
       * Register a pending tour timeout
       * @param tourId - ID of the tour
       * @param timeoutId - The setTimeout ID
       */
      registerPendingTimeout(tourId: string, timeoutId: number): void {
         // If timeoutId is -1, it means the timeout has completed, so remove it
         if (timeoutId === -1) {
            delete this.pendingTourTimeouts[tourId]
            return
         }

         this.pendingTourTimeouts[tourId] = timeoutId
      },

      /**
       * Cancel all active and pending tours
       * Used when navigating between pages
       */
      cancelAllTours(): void {
         // Cancel all pending timeouts
         Object.entries(this.pendingTourTimeouts).forEach(
            ([tourId, timeoutId]) => {
               console.log(`Canceling pending tour start: ${tourId}`)
               clearTimeout(timeoutId)
               delete this.pendingTourTimeouts[tourId]
            },
         )

         // Cancel all active tours
         Object.entries(this.activeTourInstances).forEach(
            ([tourId, instance]) => {
               if (instance?.isActive()) {
                  console.log(`Canceling active tour: ${tourId}`)
                  this.endTour(tourId)
                  instance.cancel()
               }
            },
         )
      },
   },
})
